import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { shouldRenderMonolithRestaurant } from './utils/designKit';
import { buildWebCreatorPublicUrl, fetchWebCreatorPublic } from '../../api/webCreator';
import WebCreatorSitePageView from './components/WebCreatorSitePageView';
import RestaurantExempleView from './components/restaurant-exemple/RestaurantExempleView';
import { getRestaurantPageComponent } from './utils/restaurantExempleDefaults';
import ClickCollectPublicView from './components/ClickCollectPublicView';
import ReservationPublicView from './components/ReservationPublicView';
import JustOnePageOverflowMenu from './components/JustOnePageOverflowMenu';
import { migrateSite } from './utils/defaults';
import {
  buildGeoJsonLd,
  getGeoRobotMetaTags,
  getGeoSummaryMeta,
  getGeoTopicsMeta,
} from './utils/geoHead';
import Icon from '../../components/AppIcon';
import { getCommerceCapabilities, siteHasCommerceAdmin } from './utils/commerce';

function applyPublicResponse(res, setSite, setMeta) {
  setSite(migrateSite(res?.data?.site));
  setMeta({
    isOwner: Boolean(res?.data?.isOwner),
    siteId: String(res?.data?.siteId || ''),
    published: Boolean(res?.data?.published),
    commerceAdminAvailable: Boolean(res?.data?.commerceAdminAvailable),
  });
}

export default function WebCreatorPublic() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isCommandePage = location.pathname.replace(/\/$/, '').endsWith('/commande');
  const [site, setSite] = useState(null);
  const [meta, setMeta] = useState({ isOwner: false, siteId: '', published: false });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [publicView, setPublicView] = useState('site');
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const loadPublic = useCallback(async () => {
    try {
      const res = await fetchWebCreatorPublic(slug);
      if (!aliveRef.current) return;
      applyPublicResponse(res, setSite, setMeta);
      setError('');
    } catch (err) {
      if (!aliveRef.current) return;
      setError(err.message || 'Page introuvable.');
    }
  }, [slug]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setPublicView('site');

    fetchWebCreatorPublic(slug)
      .then((res) => {
        if (cancelled) return;
        applyPublicResponse(res, setSite, setMeta);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Page introuvable.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const openCommerceAdmin = () => {
    if (meta.siteId) {
      navigate(`/web-creator/edit/${meta.siteId}/administration`);
    }
  };

  const pageTitle = site
    ? site.seo?.shareTitle?.trim() || site.title || 'Landing page'
    : loading
      ? 'Chargement…'
      : 'Page introuvable';
  const metaDescription = site
    ? site.description?.trim() || site.seo?.shareDescription?.trim() || ''
    : '';
  const ogTitle = site
    ? site.seo?.shareTitle?.trim() || site.title || pageTitle
    : pageTitle;
  const ogDescription = site
    ? site.seo?.shareDescription?.trim() || site.description?.trim() || ''
    : '';
  const ogImage = site?.seo?.shareImageUrl?.trim() || '';
  const pageUrl = site?.slug ? buildWebCreatorPublicUrl(site.slug) : '';
  const geoJsonLd = site && pageUrl ? buildGeoJsonLd(site, pageUrl) : null;
  const geoRobotTags = site ? getGeoRobotMetaTags(site.geo) : [];
  const geoSummaryMeta = site ? getGeoSummaryMeta(site.geo) : null;
  const geoTopicsMeta = site ? getGeoTopicsMeta(site.geo) : null;

  const showOwnerMenu =
    site &&
    !loading &&
    !error &&
    meta.isOwner &&
    (meta.commerceAdminAvailable || siteHasCommerceAdmin(site));

  const commerceCapabilities = site ? getCommerceCapabilities(site) : null;
  const monolithRestaurant = site ? shouldRenderMonolithRestaurant(site) : false;
  const restaurantPageComp =
    site && monolithRestaurant ? getRestaurantPageComponent(site) : null;

  let mainContent = null;

  if (loading) {
    mainContent = (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        <Icon name="Loader2" size={20} className="animate-spin mr-2" />
        Chargement…
      </div>
    );
  } else if (error || !site) {
    mainContent = (
      <div className="min-h-screen flex items-center justify-center p-6 text-center">
        <div>
          <h1 className="text-lg font-semibold text-foreground">Page introuvable</h1>
          <p className="text-sm text-muted-foreground mt-2">{error}</p>
        </div>
      </div>
    );
  } else if (isCommandePage && commerceCapabilities?.clickAndCollect) {
    mainContent = (
      <ClickCollectPublicView
        slug={slug}
        site={site}
        siteTitle={site.title}
        items={site.clickAndCollect?.items || []}
        pageHeaderLabel="La carte"
        onBack={() => navigate(`/lp/${encodeURIComponent(slug)}`)}
        onOrderComplete={loadPublic}
      />
    );
  } else if (restaurantPageComp && !isCommandePage) {
    mainContent = (
      <RestaurantExempleView
        props={restaurantPageComp.props}
        site={site}
        slug={slug}
        interactive
        commandeUrl={`/lp/${encodeURIComponent(slug)}/commande`}
      />
    );
  } else if (publicView === 'click-collect' && commerceCapabilities?.clickAndCollect) {
    mainContent = (
      <ClickCollectPublicView
        slug={slug}
        site={site}
        siteTitle={site.title}
        items={site.clickAndCollect?.items || []}
        onBack={() => setPublicView('site')}
        onOrderComplete={loadPublic}
      />
    );
  } else if (publicView === 'reservation' && commerceCapabilities?.reservation) {
    mainContent = (
      <ReservationPublicView
        slug={slug}
        site={site}
        siteTitle={site.title}
        reservation={site.reservation || {}}
        onBack={() => setPublicView('site')}
      />
    );
  } else {
    mainContent = (
      <WebCreatorSitePageView
        site={site}
        interactive
        checkoutSlug={site.slug}
        onOrderComplete={loadPublic}
        onCommerceNavigate={(view) => {
          if (view === 'click-collect' && commerceCapabilities?.clickAndCollect) {
            if (restaurantPageComp) {
              navigate(`/lp/${encodeURIComponent(slug)}/commande`);
              return;
            }
            setPublicView('click-collect');
          }
          if (view === 'reservation' && commerceCapabilities?.reservation) {
            setPublicView('reservation');
          }
        }}
        showBrandingFooter
      />
    );
  }

  return (
    <div className="min-h-screen relative">
      <Helmet key={`wc-public-${slug}`}>
        <title>{pageTitle}</title>
        {site && metaDescription ? <meta name="description" content={metaDescription} /> : null}
        {geoSummaryMeta ? (
          <meta name={geoSummaryMeta.name} content={geoSummaryMeta.content} />
        ) : null}
        {geoTopicsMeta ? <meta name={geoTopicsMeta.name} content={geoTopicsMeta.content} /> : null}
        {geoRobotTags.map((tag) => (
          <meta key={tag.name} name={tag.name} content={tag.content} />
        ))}
        {geoJsonLd ? (
          <script type="application/ld+json">{JSON.stringify(geoJsonLd)}</script>
        ) : null}
        {site && pageUrl ? (
          <>
            <meta property="og:type" content="website" />
            <meta property="og:url" content={pageUrl} />
            <meta property="og:title" content={ogTitle} />
            {ogDescription ? <meta property="og:description" content={ogDescription} /> : null}
            {ogImage ? <meta property="og:image" content={ogImage} /> : null}
            <meta name="twitter:card" content={ogImage ? 'summary_large_image' : 'summary'} />
            <meta name="twitter:title" content={ogTitle} />
            {ogDescription ? <meta name="twitter:description" content={ogDescription} /> : null}
            {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}
          </>
        ) : null}
      </Helmet>

      {showOwnerMenu ? (
        <div className="fixed bottom-5 right-5 z-50">
          <JustOnePageOverflowMenu
            buttonClassName="bg-card border border-border shadow-elevation-2"
            items={[
              {
                id: 'admin',
                label: 'Administration',
                icon: 'Store',
                onClick: openCommerceAdmin,
              },
            ]}
          />
        </div>
      ) : null}

      {mainContent}
    </div>
  );
}
